import {
  CompanyPensionProduct,
  ComputedFees,
  FeeInformation,
} from "../types/CompanyPensionProduct";
import { FeeType } from "../data/FeeType";

export default function computeFees(
  product: CompanyPensionProduct,
  savingsAmount: number,
): ComputedFees {
  const managementFee = calculateFeeByPercentOfSavings(
    product.lowestManagementFeeOnGlobalEquityFund,
    savingsAmount,
  );
  const platformFee = calculateFeeByPercentOfSavings(
    product.platformFee,
    savingsAmount,
  );
  const annualAdministrationFee = calculateAnnualAdministrationFee(
    product.fees,
    savingsAmount,
  );
  const totalFee = managementFee + platformFee + annualAdministrationFee;

  return {
    managementFee: {
      amount: managementFee,
      percent: calculatePercentage(managementFee, savingsAmount),
    },
    platformFee: {
      amount: platformFee,
      percent: calculatePercentage(platformFee, savingsAmount),
    },
    annualAdministrationFee: {
      amount: annualAdministrationFee,
      percent: calculatePercentage(annualAdministrationFee, savingsAmount),
    },
    totalFee: {
      amount: totalFee,
      percent: calculatePercentage(totalFee, savingsAmount),
    },
  };

  function calculateFeeByPercentWithMinimumFixedFee(
    fees: FeeInformation,
    savingsAmount: number,
  ) {
    const percentageAmount = calculateFeeByPercentOfSavings(
      fees.pwmfpercentage,
      savingsAmount,
    );
    const fixedFee = fees.pwmffixed ?? 0;

    return fixedFee > percentageAmount ? fixedFee : percentageAmount;
  }

  function calculateAnnualAdministrationFee(
    fees: FeeInformation,
    savingsAmount: number,
  ) {
    switch (fees.feeType) {
      case FeeType.NONE:
        return 0;
      case FeeType.PERCENTAGE:
        return calculateFeeByPercentOfSavings(fees.percentage, savingsAmount);
      case FeeType.PERCENTAGE_AND_FIXED:
        return calculateFeeByPercentWithFixedFee(fees, savingsAmount);
      case FeeType.PERCENTAGE_WITH_MIN_FIXED:
        return calculateFeeByPercentWithMinimumFixedFee(fees, savingsAmount);
      case FeeType.FIXED:
        return fees.fixedFee ?? 0;
      case FeeType.FIXED_WITH_LIMIT:
        return calculateFeeByFixedWithUpperLimit(
          fees.fixedlimitfee,
          fees.fixedlimitpercentlimit,
          savingsAmount,
        );
      case FeeType.FIXED_DUAL_LIMIT:
        return calculateFeeByFixedDualUpperLimit(fees, savingsAmount);
      default:
        return 0;
    }
  }

  function calculateFeeByFixedWithUpperLimit(
    fixedFee: number | null,
    fixedFeePercentLimit: number | null,
    savingsAmount: number,
  ) {
    const upperLimit = calculateFeeByPercentOfSavings(
      fixedFeePercentLimit,
      savingsAmount,
    );
    const fixedFeeLimit = fixedFee ?? 0;

    return fixedFeeLimit > upperLimit ? upperLimit : fixedFeeLimit;
  }

  function calculateFeeByFixedDualUpperLimit(
    fees: FeeInformation,
    savingsAmount: number,
  ) {
    const primaryFee = calculateFeeByFixedWithUpperLimit(
      fees.primarydualfixedfee,
      fees.primarydualfixedfeelimit,
      savingsAmount,
    );
    const secondaryFee = calculateFeeByFixedWithUpperLimit(
      fees.secondarydualfixedfee,
      fees.secondarydualfixedfeelimit,
      savingsAmount,
    );

    return primaryFee + secondaryFee;
  }

  function calculateFeeByPercentWithFixedFee(
    fees: FeeInformation,
    savingsAmount: number,
  ) {
    return (
      calculateFeeByPercentOfSavings(fees.pnfpercentage, savingsAmount) +
      (fees.pnffixed ?? 0)
    );
  }

  function calculateFeeByPercentOfSavings(
    feeInPercent: number | null,
    savingsAmount: number,
  ) {
    return feeInPercent ? (feeInPercent * savingsAmount) / 100 : 0;
  }
  function calculatePercentage(
    feeAmount: number | null,
    savingsAmount: number,
  ) {
    return feeAmount ? (feeAmount / savingsAmount) * 100 : 0;
  }
}
